/* ============================================================
   147Pot Product Add-on Choice
   Version: 1.2
   File: assets/147-product-addon-choice.js
   ============================================================

   PURPOSE
   -------
   This file controls the PRODUCT PAGE UI only.

   FEATURES
   --------
   - Multiple independent add-on groups
   - One selection per group
   - "None" selections supported
   - Selected/unselected card state
   - Live Bundle Total
   - Live add-on savings total
   - Original Bundle Total before displayed savings
   - Responds to main product quantity changes
   - Responds to main product variant changes

   IMPORTANT — CART SUBMISSION
   ---------------------------
   This file deliberately DOES NOT submit the product form.

   All Add to Cart behaviour is handled by product-form.js.

   This prevents two separate scripts from competing over the
   same Shopify product form.

   IMPORTANT — DISCOUNTS
   ---------------------
   Discounted add-on prices calculated here are DISPLAY /
   CALCULATION prices only.

   The corresponding real discount must be configured in
   Shopify so that cart / checkout pricing matches the amount
   displayed on the product page.
   ============================================================ */

(() => {
  if (window.__a147AddonChoiceInit) {
    return;
  }

  window.__a147AddonChoiceInit = true;


  /* ==========================================================
     MONEY FORMATTING
     ========================================================== */

  const money = (cents) => {
    const amount = Number(cents || 0);

    try {
      return new Intl.NumberFormat(
        document.documentElement.lang || 'en-GB',
        {
          style: 'currency',

          currency:
            window.Shopify?.currency?.active ||
            'GBP',
        }
      ).format(amount / 100);

    } catch (error) {

      return '£' + (amount / 100).toFixed(2);
    }
  };


  /* ==========================================================
     FIND PRODUCT SECTIONS
     ========================================================== */

  const getProductRoots = () => {
    return document.querySelectorAll(
      'product-info'
    );
  };


  /* ==========================================================
     MAIN PRODUCT PRICE
     ========================================================== */

  const getMainProductPrice = (root) => {
    const variantInput = root.querySelector(
      'form[data-type="add-to-cart-form"] .product-variant-id'
    );

    const variantId =
      variantInput?.value;

    const variantData = root.querySelector(
      '[data-a147-main-variants]'
    );


    /*
      main-product.liquid supplies a JSON map:

      {
        "variant-id": price-in-cents,
        ...
      }

      This allows Bundle Total to follow the currently selected
      main-product variant.
    */

    if (variantData && variantId) {
      try {
        const prices = JSON.parse(
          variantData.textContent
        );

        if (
          Object.prototype.hasOwnProperty.call(
            prices,
            variantId
          )
        ) {
          return Number(
            prices[variantId] || 0
          );
        }

      } catch (error) {

        console.warn(
          '147Pot add-on choice: unable to read main variant prices.',
          error
        );
      }
    }


    /*
      Fallback supplied by main-product.liquid.
    */

    return Number(
      root.dataset.a147BasePrice || 0
    );
  };


  /* ==========================================================
     MAIN PRODUCT QUANTITY
     ========================================================== */

  const getMainQuantity = (root) => {
    const quantityInput = root.querySelector(
      '[id^="Quantity-"]'
    );

    const quantity = Number(
      quantityInput?.value || 1
    );

    return Math.max(
      1,
      Number.isFinite(quantity)
        ? quantity
        : 1
    );
  };


  /* ==========================================================
     SELECTED CHOICE ADD-ONS
     ========================================================== */

  const getSelectedAddons = (root) => {
    return [
      ...root.querySelectorAll(
        '[data-a147-addon-radio]:checked'
      ),
    ]

      /*
        Ignore "Without..." selections.

        Their radio value / variant ID is intentionally empty.
      */

      .filter((radio) => {
        return Boolean(
          radio.value &&
          radio.dataset.variantId
        );
      })

      .map((radio) => {
        return {
          id: Number(
            radio.dataset.variantId
          ),

          price: Number(
            radio.dataset.price || 0
          ),

          displayPrice: Number(
            radio.dataset.displayPrice ||
            radio.dataset.price ||
            0
          ),

          title:
            radio.dataset.title ||
            'Add-on',
        };
      })

      .filter((addon) => {
        return (
          Number.isFinite(addon.id) &&
          addon.id > 0
        );
      });
  };


  /* ==========================================================
     SELECTED CARD STYLING
     ========================================================== */

  const updateSelectedCards = (root) => {
    root
      .querySelectorAll(
        '[data-a147-addon-radio]'
      )
      .forEach((radio) => {
        const option = radio.closest(
          '.a147-addon-choice__option'
        );

        if (!option) {
          return;
        }

        option.classList.toggle(
          'is-selected',
          radio.checked
        );
      });
  };


  /* ==========================================================
     BUNDLE TOTAL
     ========================================================== */

  const updateBundleTotal = (root) => {
    const summary = root.querySelector(
      '[data-a147-addon-summary]'
    );

    if (!summary) {
      return;
    }


    /* --------------------------------------------------------
       Main product
       -------------------------------------------------------- */

    const mainUnitPrice =
      getMainProductPrice(root);

    const mainQuantity =
      getMainQuantity(root);

    const mainTotal =
      mainUnitPrice * mainQuantity;


    /* --------------------------------------------------------
       Selected choice add-ons
       -------------------------------------------------------- */

    const addons =
      getSelectedAddons(root);

    let addonsTotal = 0;
    let addonsOriginalTotal = 0;
    let savingTotal = 0;


    addons.forEach((addon) => {

      addonsTotal +=
        addon.displayPrice;

      addonsOriginalTotal +=
        addon.price;

      savingTotal += Math.max(
        0,
        addon.price -
          addon.displayPrice
      );

    });


    /* --------------------------------------------------------
       Package totals
       -------------------------------------------------------- */

    const bundleTotal =
      mainTotal + addonsTotal;

    const originalBundleTotal =
      mainTotal + addonsOriginalTotal;


    /* --------------------------------------------------------
       Main product row
       -------------------------------------------------------- */

    const mainPriceElement =
      summary.querySelector(
        '[data-a147-main-total]'
      );

    if (mainPriceElement) {
      mainPriceElement.textContent =
        money(mainTotal);
    }


    /* --------------------------------------------------------
       Selected add-ons subtotal
       -------------------------------------------------------- */

    const addonTotalElement =
      summary.querySelector(
        '[data-a147-addon-subtotal]'
      );

    if (addonTotalElement) {
      addonTotalElement.textContent =
        money(addonsTotal);
    }


    /* --------------------------------------------------------
       Discounted Bundle Total
       -------------------------------------------------------- */

    const bundleTotalElement =
      summary.querySelector(
        '[data-a147-addon-total]'
      );

    if (bundleTotalElement) {
      bundleTotalElement.textContent =
        money(bundleTotal);
    }


    /* --------------------------------------------------------
       Original Bundle Total
       -------------------------------------------------------- */

    const originalTotalElement =
      summary.querySelector(
        '[data-a147-addon-original-total]'
      );

    if (originalTotalElement) {

      originalTotalElement.textContent =
        money(originalBundleTotal);

      /*
        Don't show an identical crossed-out total when there is
        no displayed saving.
      */

      originalTotalElement.hidden =
        savingTotal <= 0;
    }


    /* --------------------------------------------------------
       Bundle Saving
       -------------------------------------------------------- */

    const savingElement =
      summary.querySelector(
        '[data-a147-addon-saving]'
      );

    if (savingElement) {

      savingElement.textContent =
        money(savingTotal);

      const savingRow =
        savingElement.closest(
          '.a147-addon-summary__saving'
        );

      if (savingRow) {
        savingRow.hidden =
          savingTotal <= 0;
      }
    }


    /* --------------------------------------------------------
       Optional Add-ons subtotal row
       -------------------------------------------------------- */

    const addonsRow =
      summary.querySelector(
        '[data-a147-addon-subtotal-row]'
      );

    if (addonsRow) {
      addonsRow.hidden =
        addons.length === 0;
    }
  };


  /* ==========================================================
     UPDATE COMPLETE PRODUCT UI
     ========================================================== */

  const updateProduct = (root) => {
    updateSelectedCards(root);
    updateBundleTotal(root);
  };


  /* ==========================================================
     BIND ONE PRODUCT SECTION
     ========================================================== */

  const bindProduct = (root) => {
    if (
      root.dataset.a147AddonBound
    ) {
      return;
    }

    root.dataset.a147AddonBound =
      '1';


    /*
      Choice selection / main variant / quantity changes.
    */

root.addEventListener(
  'change',
  (event) => {

    if (
      event.target.matches(
        '[data-a147-addon-radio], .product-variant-id, [id^="Quantity-"]'
      )
    ) {
      /*
        Allow the browser to finish updating the checked radio
        state before recalculating selected cards and Bundle Total.

        This is particularly important inside Shopify's Theme
        Editor, where block interactions can occur alongside
        editor selection events.
      */
      requestAnimationFrame(() => {
        updateProduct(root);
      });
    }

  }
);
/*
  Explicitly recalculate when an add-on option is clicked.

  The normal change event remains the primary listener, but
  this ensures the live Bundle Total also stays in sync inside
  Shopify's Theme Editor.
*/

root.addEventListener(
  'click',
  (event) => {
    const option = event.target.closest(
      '.a147-addon-choice__option'
    );

    if (!option) {
      return;
    }

    requestAnimationFrame(() => {
      updateProduct(root);
    });
  }
);


    /*
      Quantity controls may trigger an input event while the
      shopper is typing or while Dawn's quantity controls are
      updating the field.
    */

    root.addEventListener(
      'input',
      (event) => {

        if (
          event.target.matches(
            '[id^="Quantity-"]'
          )
        ) {
          updateBundleTotal(root);
        }

      }
    );


    /*
      Initial calculation.

      IMPORTANT:
      There is deliberately NO product-form submit listener in
      this file.

      product-form.js is the single cart-submission authority.
    */

    updateProduct(root);
  };


  /* ==========================================================
     INITIALISE
     ========================================================== */

  const init = () => {
    getProductRoots().forEach(
      (root) => {
        bindProduct(root);
      }
    );
  };


  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      init,
      {
        once: true,
      }
    );

  } else {

    init();

  }


  /* ==========================================================
     SHOPIFY THEME EDITOR
     ========================================================== */

document.addEventListener(
  'shopify:section:load',
  init
);


/* ==========================================================
   BROWSER BACK / FORWARD CACHE
   ==========================================================

   Browsers can restore a product page from memory when the
   shopper presses Back.

   In that situation the normal page initialisation may not
   run again, while form controls and CSS states can have been
   restored by the browser.

   Recalculate the 147 choice UI whenever the page becomes
   visible again.
   ========================================================== */

window.addEventListener(
  'pageshow',
  () => {
    getProductRoots().forEach(
      (root) => {
        updateProduct(root);
      }
    );
  }
);


/*
  IMPORTANT:

  Do not add a broad MutationObserver here.

  Bundle Total updates change DOM text themselves. Watching
  the entire document and recalculating from those mutations
  can create an update loop inside Shopify's Theme Editor.

  The explicit event listeners above are sufficient.
*/


})();